const logging = require('@sstdev/lib_logging');
const dataAccessMethods = require('../../../dataAccessMethods');
const mdcConfig = require('@sstdev/lib_metadata-config');
const { config } = mdcConfig;
const baseMdcBlockGroup = require('../_BaseMdcBlockGroup');
const Blockly = require('blockly');
const setupAddCodeProperties = require('../../../helpers/addCodeProperties');
const convertToBoolean = require('../../../helpers/convertToBoolean');

const types = require('./types');

const TOOLTIPS = {
    MarkRecord:
        'Find a record by one or more properties. Custom Middleware sets values from Search Elements (e.g. Location) when found or creates a new record if not found (FLAIR/AT "Take").',
    KeySearch: 'Search field to filter a grid on one or more properties, as characters are typed.',
    DateRangePicker: 'OBSOLETE: Use `Range` Action Element instead.',
    MultiPickerTransactionGrid: 'Used to select and display records for a transaction.'
};

class ActionElement extends baseMdcBlockGroup {
    /**
     * @param {{[key: string]: any}} code
     * @param {import('blockly').Block} blocklyBlock
     * @param {string} hNodeType
     */
    addCodeGeneratorCustom(code, blocklyBlock, hNodeType) {
        if (types[hNodeType]?.convertCustomFieldsToCode) {
            const resultCode = types[hNodeType].convertCustomFieldsToCode(code, blocklyBlock);
            if (typeof resultCode === 'string') {
                logging.error(
                    `${hNodeType}.convertCustomFieldsToCode should not return a string. It should return a JSON object instead.`
                );
                return resultCode;
            }
            return JSON.stringify(resultCode);
        }

        const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);
        // These are most common, but we overwrite/clear for specific HNodes
        addCodeProperties(['namespace', 'relation', 'forAction']);

        if (hNodeType === 'MultiPickerTransactionGrid') {
            addCodeProperties([
                'sensorType',
                { key: 'displayShowUnknownTags', handler: convertToBoolean },
                'textForShowUnknownTagsCheckbox',
                'rowClickAction',
                { key: 'namespace', handler: () => 'sensor' },
                { key: 'relation', handler: () => 'read' },
                { key: 'displayScanButton', handler: convertToBoolean },
                { key: 'displayRfidPowerButton', handler: convertToBoolean },
                { key: 'scanRfid', handler: convertToBoolean },
                { key: 'scanBarcode', handler: convertToBoolean },
                { key: 'includeCheckboxColumn', handler: convertToBoolean },
                { key: 'forAction', handler: () => 'create' }
            ]);
        }

        // Buttons have style and need an icon name if they are icons
        if (hNodeType.includes('Button') || ['MarkRecord'].includes(hNodeType)) {
            if (hNodeType !== 'InfoDisplayButton') {
                addCodeProperties([{ key: 'style', value: 'buttonStyle' }, 'size', 'variant', 'iconName']);
                addCodeProperties([{ key: 'showBusyUntilFinished', handler: convertToBoolean }]);
                addCodeProperties([
                    'shortcutKey',
                    { key: 'shiftButton', handler: convertToBoolean },
                    { key: 'controlButton', handler: convertToBoolean },
                    { key: 'altButton', handler: convertToBoolean }
                ]);
            }
        }

        if (hNodeType === 'DateRangePicker') {
            addCodeProperties(['propertyName', { key: 'displayActionButton', handler: convertToBoolean }]);
        }

        if (hNodeType === 'SensorReadButton') {
            addCodeProperties([
                { key: 'namespace', handler: () => 'sensor' },
                { key: 'relation', handler: () => 'read' },
                { key: 'forAction', handler: () => 'refresh' },
                'sensorType',
                'scanType',
                'intervalMilliseconds'
            ]);
        }

        if (hNodeType === 'Range') {
            // @ts-ignore
            code.fromInputHNode = JSON.parse(Blockly.JavaScript.statementToCode(blocklyBlock, 'from'));
            // @ts-ignore
            code.toInputHNode = JSON.parse(Blockly.JavaScript.statementToCode(blocklyBlock, 'to'));
            addCodeProperties(['propertyName', { key: 'displayActionButton', handler: convertToBoolean }]);
        }

        if (hNodeType === 'KeySearch') {
            // @ts-ignore
            code.filters = JSON.parse(`[${Blockly.JavaScript.statementToCode(blocklyBlock, 'filters')}]`);
            addCodeProperties([
                'tooltip',
                { key: 'scanRfid', handler: convertToBoolean },
                { key: 'scanBarcode', handler: convertToBoolean }
            ]);
        }

        if (hNodeType === 'MarkRecord') {
            // @ts-ignore
            code.transforms = JSON.parse(`[${Blockly.JavaScript.statementToCode(blocklyBlock, 'transforms')}]`);
            addCodeProperties([
                { key: 'displayScanButton', handler: convertToBoolean },
                { key: 'displayRfidPowerButton', handler: convertToBoolean },
                { key: 'scanRfid', handler: convertToBoolean },
                { key: 'scanBarcode', handler: convertToBoolean }
            ]);
        }

        if (hNodeType === 'Slider') {
            addCodeProperties([
                { key: 'namespace', handler: () => 'sensor' },
                { key: 'relation', handler: () => 'service' },
                { key: 'forAction', handler: () => 'change' },
                'propertyName',
                'minIconName',
                'maxIconName',
                { key: 'max', handler: parseInt },
                { key: 'min', handler: parseInt },
                { key: 'defaultValue', handler: parseInt }
            ]);
        }

        return JSON.stringify(code);
    }

    /**
     * @param {*} color
     * @param {import('blockly').Block} blocklyBlock
     * @param {string} hNodeType
     */
    addToBlocksCustom(color, blocklyBlock, hNodeType, mdcBlock, topInput) {
        if (types[hNodeType]) {
            types[hNodeType].addBlockSpecificFields(color, blocklyBlock, mdcBlock, topInput);
            return;
        }
        let blockId = blocklyBlock.id;
        let workspace = blocklyBlock.workspace;

        if (TOOLTIPS[hNodeType]) {
            blocklyBlock.setTooltip(TOOLTIPS[hNodeType]);
        }

        // Having namespace and relation is quite common and we want to show it first if they are applicable
        let hasNsRelation = !['Link', 'InfoDisplayButton', 'SensorReadButton', 'MultiPickerTransactionGrid'].includes(
            hNodeType
        );
        if (hasNsRelation) {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Namespace:')
                .appendField(new Blockly.FieldDropdown(dataAccessMethods.getNamespaces), 'namespace')
                .appendField('  Relation:')
                .appendField(
                    new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId)),
                    'relation'
                );
        }

        if (hNodeType === 'MultiPickerTransactionGrid') {
            const rowClickDropDown = new Blockly.FieldDropdown(config.actionElementActions);
            rowClickDropDown.setValue('edit');
            topInput.appendField('Row Click Action:').appendField(rowClickDropDown, 'rowClickAction');

            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Display "Show Unknown Tags" Checkbox: ')
                .appendField(new Blockly.FieldCheckbox(false), 'displayShowUnknownTags')
                .appendField('  Text for "Show Unknown Tags" Checkbox: ')
                .appendField(new Blockly.FieldTextInput('Show Unknown Tags'), 'textForShowUnknownTagsCheckbox')
                .appendField('  Include Checkbox Column:')
                .appendField(new Blockly.FieldCheckbox(false), 'includeCheckboxColumn');

            blocklyBlock.setInputsInline(false);
            blocklyBlock.appendStatementInput('columns').setCheck(['listColumn']).appendField('List Columns:');
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Display Scan Button')
                .appendField(new Blockly.FieldCheckbox(), 'displayScanButton')
                .appendField('  Display RFID Power Button')
                .appendField(new Blockly.FieldCheckbox(), 'displayRfidPowerButton')
                .appendField('  Scan RFID')
                .appendField(new Blockly.FieldCheckbox(), 'scanRfid')
                .appendField('  Scan Barcode')
                .appendField(new Blockly.FieldCheckbox(), 'scanBarcode');
        }

        // Add style and icon name properties for buttons
        if (hNodeType.includes('Button') || ['MarkRecord'].includes(hNodeType)) {
            // Set button style
            let styleDropDown = new Blockly.FieldDropdown(config.buttonStyles);
            if (['DownloadButton', 'SensorReadButton'].includes(hNodeType)) styleDropDown.setValue('IconAction');
            if (hNodeType !== 'InfoDisplayButton') {
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Button Style (old):')
                    .appendField(styleDropDown, 'buttonStyle')
                    .appendField('  Button Size (new):')
                    .appendField(new Blockly.FieldDropdown(config.componentSize), 'size')
                    .appendField('  Button Variant (new):')
                    .appendField(new Blockly.FieldDropdown(config.buttonVariants), 'variant');
            }
            // Set icon name if necessary
            let defaultIconName = '';

            // speaker_phone looks like a handheld device shooting out a signal
            if (hNodeType === 'SensorReadButton') defaultIconName = 'speaker_phone';

            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Icon Name:')
                .appendField(new Blockly.FieldTextInput(defaultIconName), 'iconName');

            if (!['InfoDisplayButton', 'ImportForm'].includes(hNodeType)) {
                blocklyBlock
                    .appendDummyInput()
                    .appendField('  Busy Animation Until Finished')
                    .appendField(new Blockly.FieldCheckbox(), 'showBusyUntilFinished')
                    .appendField('  Shortcut')
                    .appendField(new Blockly.FieldTextInput(), 'shortcutKey')
                    .appendField('  Shift Button')
                    .appendField(new Blockly.FieldCheckbox(), 'shiftButton')
                    .appendField('  Control Button')
                    .appendField(new Blockly.FieldCheckbox(), 'controlButton')
                    .appendField('  Alt Button')
                    .appendField(new Blockly.FieldCheckbox(), 'altButton');
            }
        }

        if (hNodeType === 'SensorReadButton') {
            blocklyBlock
                .appendDummyInput()
                .appendField('  Sensor Type:')
                .appendField(
                    new Blockly.FieldDropdown([
                        ['RFID', 'RFID'],
                        ['BLE', 'BLE']
                    ]),
                    'sensorType'
                )
                .appendField('  Scan type:')
                .appendField(
                    new Blockly.FieldDropdown([
                        ['Continuous', 'Continuous'],
                        ['On Request', 'OnRequest']
                    ]),
                    'scanType'
                )
                .appendField('  Scan Length (milliseconds):')
                .appendField(new Blockly.FieldNumber(6000, 500, 30000, 500), 'intervalMilliseconds');
            return;
        }

        if (['DateRangePicker', 'Range'].includes(hNodeType)) {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Property Name:')
                .appendField(new Blockly.FieldTextInput('meta.createdTime'), 'propertyName');

            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Display Action Button:')
                .appendField(new Blockly.FieldCheckbox('true'), 'displayActionButton');
        }

        // We don't need this check, but adding it for documentation and as placeholder for future exceptions
        const hasForAction = !['SensorReadButton', 'MultiPickerTransactionGrid', 'InfoDisplayButton'].includes(
            hNodeType
        );
        if (hasForAction) {
            // Load possible actions and default action
            const actionsDropdown = new Blockly.FieldDropdown(config.actionElementActions);
            actionsDropdown.setValue('filter');
            if (hNodeType.includes('Button')) actionsDropdown.setValue('new');

            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  For Action:')
                .appendField(actionsDropdown, 'forAction');
        }

        if (hNodeType === 'MarkRecord') {
            blocklyBlock.appendStatementInput('filters').setCheck(['filter']).appendField('Filters:');
            blocklyBlock.appendStatementInput('transforms').setCheck(['transform']).appendField('Transforms:');
        }

        if (hNodeType === 'Range') {
            // @ts-ignore
            blocklyBlock.additionalListNames = ['from', 'to'];
            blocklyBlock
                .appendStatementInput('from')
                .setCheck(['rangeType'])
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('From:', 'from');
            blocklyBlock
                .appendStatementInput('to')
                .setCheck(['rangeType'])
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('To:', 'to');
        }

        if (hNodeType === 'KeySearch') {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Tooltip')
                .appendField(new Blockly.FieldTextInput(), 'tooltip');
            blocklyBlock.appendStatementInput('filters').setCheck(['filter']).appendField('Filters:');
        }

        if (hNodeType === 'Slider') {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Property Name:')
                .appendField(new Blockly.FieldTextInput('power'), 'propertyName');
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Min:')
                .appendField(new Blockly.FieldNumber('0', Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, 1), 'min');
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Max:')
                .appendField(new Blockly.FieldNumber(100, Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, 1), 'max');
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Default Value:')
                .appendField(
                    new Blockly.FieldNumber(100, Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, 1),
                    'defaultValue'
                );
        }
        if (['DateRangePicker'].includes(hNodeType)) {
            blocklyBlock.setEnabled(false);
        }

        if (['KeySearch'].includes(hNodeType)) {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Scan RFID')
                .appendField(new Blockly.FieldCheckbox(), 'scanRfid')
                .appendField('  Scan Barcode')
                .appendField(new Blockly.FieldCheckbox(), 'scanBarcode');
        }

        if (['MarkRecord'].includes(hNodeType)) {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Display Scan Button')
                .appendField(new Blockly.FieldCheckbox(), 'displayScanButton')
                .appendField('  Display RFID Power Button')
                .appendField(new Blockly.FieldCheckbox(), 'displayRfidPowerButton')
                .appendField('  Scan RFID')
                .appendField(new Blockly.FieldCheckbox(), 'scanRfid')
                .appendField('  Scan Barcode')
                .appendField(new Blockly.FieldCheckbox(), 'scanBarcode');
        }
    }
    customizeToolbox(section) {
        const blockForFindRecord = section.contents.find(b => b.type === 'actionElement_FindRecord');
        const blockForMarkRecord = section.contents.find(b => b.type === 'actionElement_MarkRecord');
        const blockForRange = section.contents.find(b => b.type === 'actionElement_Range');
        const blockForKeySearch = section.contents.find(b => b.type === 'actionElement_KeySearch');
        const blockForMenuButton = section.contents.find(b => b.type === 'actionElement_MenuButton');

        if (blockForFindRecord) {
            blockForFindRecord.inputs = {
                filters: {
                    block: {
                        type: 'filter_PropertiesToSearch',
                        fields: { propertiesToSearch: 'title' },
                        next: {
                            block: {
                                type: 'filter_OrderBy',
                                fields: { propertyName: 'title' }
                            }
                        }
                    }
                }
            };
        }

        if (blockForKeySearch) {
            blockForKeySearch.inputs = {
                filters: {
                    block: {
                        type: 'filter_PropertiesToSearch',
                        fields: { propertiesToSearch: 'assetNo,serialNo' }
                    }
                }
            };
        }

        if (blockForMarkRecord) {
            blockForMarkRecord.inputs = {
                filters: {
                    block: {
                        type: 'filter_PropertiesToSearch',
                        fields: { propertiesToSearch: 'title' }
                    }
                }
            };
        }

        if (blockForRange) {
            blockForRange.inputs = {
                from: {
                    block: {
                        type: 'rangeType_DateTime',
                        fields: { title: 'From' }
                    }
                },
                to: {
                    block: {
                        type: 'rangeType_DateTime',
                        fields: { title: 'To' }
                    }
                }
            };
        }
        if (blockForMenuButton) {
            blockForMenuButton.inputs = {
                children: {
                    block: {
                        type: 'menuSelection_MenuItem',
                        fields: { title: 'Menu Item 1' }
                    }
                }
            };
        }
    }
}

module.exports = ActionElement;
